import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { KbArticle, KbListResponse, KbImageUploadResponse } from '@/types/kb'

export const kbKeys = {
  all: ['kb'] as const,
  list: (params: Record<string, unknown>) => [...kbKeys.all, 'list', params] as const,
  bySlug: (slug: string) => [...kbKeys.all, 'slug', slug] as const,
  byId: (id: string) => [...kbKeys.all, 'id', id] as const,
}

export function useKbArticles(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: kbKeys.list(params),
    queryFn: () => api<KbListResponse>('/kb-articles', { query: params }),
  })
}

export function useKbArticle(slug: string) {
  return useQuery({
    queryKey: kbKeys.bySlug(slug),
    queryFn: () => api<KbArticle>(`/kb-articles/${slug}`),
    enabled: Boolean(slug),
  })
}

export function useCreateKbArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbArticle>) =>
      api<KbArticle>('/kb-articles', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useUpdateKbArticle(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbArticle>) =>
      api<KbArticle>(`/kb-articles/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useDeleteKbArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/kb-articles/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useUploadKbImage(articleId?: string) {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      const path = articleId ? `/kb-articles/${articleId}/images` : '/kb-articles/images'
      return api<KbImageUploadResponse>(path, { method: 'POST', body: fd })
    },
  })
}
